
















































































































































































import Super from "@/pv4-super-class"
import { Component } from "vue-property-decorator"

import {
  chain,
  includes,
  toLower,
  throttle,
  get,
  startsWith,
  stubTrue
} from "lodash"

import { EventID } from "@/types"
import { AxiosRequestConfig } from "axios"

@Component
export default class StatsExpressions extends Super {
  expression = ""
  expressionType: "handlebars" | "jsonpath" = "handlebars"
  evalResult = ""
  evalErrors = ""
  keys: string[] = []
  lastExpressionPosition = 0

  dataKeySelectorOpen = false
  keySearch = ""
  dataKeySearchIncludeRaw = false

  evaluateExpression = throttle(this._evaluateExpression, 500)

  /**
   * Get saved expressions
   */
  get savedExpressions() {
    return this.$store.getters.savedStatsExpressions
  }

  /**
   * Runs when the component is created- load keys from API
   */
  async created() {
    await this.loadKeys(this.eventId)
  }

  /**
   * Load available keys from the API for autocomplete
   */
  loadKeys(eventId: EventID): Promise<any> {
    return this.http
      .get(`/audition/data?eventID=${eventId}&keys`)
      .then(res => (this.keys = res.data))
      .catch(this.displayError)
  }

  /**
   * Evaluate an expression from the dataset
   **/
  _evaluateExpression(
    eventId: EventID,
    expression: string,
    expressionType: string
  ): Promise<any> {
    this.evalResult = ""
    this.evalErrors = ""

    if (!expression || !expressionType) return Promise.resolve(null)

    const config: AxiosRequestConfig = {
      params: { expression, expressionType }
    }

    return this.http
      .get(`/audition/data?eventID=${this.eventId}`, config)
      .then(res => (this.evalResult = res.data))
      .catch(
        error =>
          (this.evalErrors = get(
            error,
            "response.data.message",
            "Unknown Error"
          ))
      )
  }

  /**
   * Get external link
   **/
  getExpressionURL(
    eventId: EventID,
    expression: string,
    expressionType: string
  ): Promise<any> {
    if (!expression || !expressionType) return Promise.resolve(null)

    const config: AxiosRequestConfig = {
      params: { expression, expressionType }
    }

    return this.http
      .get(`/audition/data?eventID=${this.eventId}&asToken`, config)
      .then(res => {
        const token = res.data
        const url = `${this.orchestrateGateway}/stats/${token}`
        this.$store.dispatch("saveStatsExpression", { expression, token, url })
      })
      .catch(this.displayError)
  }

  /**
   * Clear saved expressions
   */
  clearSavedExpressions() {
    this.$store.dispatch("clearStatsExpressions")
  }

  /**
   * Copy a link to the clipboard
   */
  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.$buefy.notification.open({
          position: "is-bottom",
          type: "is-success",
          message: "Link copied to clipboard!"
        })
      })
      .catch(() => {
        this.$buefy.notification.open({
          position: "is-bottom",
          type: "is-danger",
          message: "Error! Couldn't copy to clipboard."
        })
      })
  }

  /**
   * Check if the string contains an [object Object] reference
   */
  hasObjectObject(evalResult: string): boolean {
    return includes(evalResult, "[object Object]")
  }

  /**
   * Open the data key selector modal
   */
  openDataKeySelector() {
    this.dataKeySelectorOpen = true
  }

  /**
   * Keep track of the caret position in the expression input field
   */
  setLastExpressionPosition(event) {
    this.lastExpressionPosition = event.target.selectionStart
    this.evaluateExpression(this.eventID, this.expression, this.expressionType)
  }

  /**
   * Action a data key selection
   */
  doDataKeySelection(selection: string) {
    this.dataKeySelectorOpen = false
    const preText = this.expression.slice(0, this.lastExpressionPosition)
    const postText = this.expression.slice(this.lastExpressionPosition)
    this.expression = `${preText}{{ ${selection} }}${postText}`
    this.evaluateExpression(this.eventID, this.expression, this.expressionType)
    this.keySearch = ""
  }

  /**
   * Filter strings that match
   */
  filterMatchingStrings(filterSearch: string) {
    return item => startsWith(toLower(item), toLower(filterSearch))
  }

  /**
   * Filter out raw keys if not wanted that match
   */
  filterRaw(enabled: boolean) {
    if (enabled) return stubTrue
    return item =>
      !startsWith(item, "data.") &&
      !startsWith(item, "palettes.") &&
      item != "data" &&
      item != "palettes"
  }

  /**
   * A filtered list of keys
   */
  filteredKeys(keys: any, filterSearch: string, filterRaw: boolean): string[] {
    return chain(keys)
      .filter(this.filterRaw(filterRaw))
      .filter(this.filterMatchingStrings(filterSearch))
      .slice(0, 7)
      .value()
  }
}
